import type { ElementType, ReactNode } from 'react'

import { cx } from '../lib/cx'

/**
 * Apilado con gap consistente. Reemplaza los `flex gap-2` a mano: la escala de
 * aire es una y la misma en todas las pantallas.
 */
export interface StackProps {
  children: ReactNode
  /** vertical por defecto. */
  direccion?: 'vertical' | 'horizontal'
  gap?: 'xs' | 'sm' | 'md' | 'lg'
  /** Alineación en el eje cruzado. */
  align?: 'start' | 'center' | 'baseline' | 'end' | 'stretch'
  /** Distribución en el eje principal. */
  justify?: 'start' | 'between' | 'end'
  /** Solo horizontal: permite que los hijos bajen de línea. */
  wrap?: boolean
  /** Elemento del DOM. `ul`/`ol` cuando el stack es una lista de verdad. */
  as?: ElementType
  className?: string
}

const GAP: Record<NonNullable<StackProps['gap']>, string> = {
  xs: 'gap-1',
  sm: 'gap-2',
  md: 'gap-4',
  lg: 'gap-6',
}

const ALIGN: Record<NonNullable<StackProps['align']>, string> = {
  start: 'items-start',
  center: 'items-center',
  baseline: 'items-baseline',
  end: 'items-end',
  stretch: 'items-stretch',
}

const JUSTIFY: Record<NonNullable<StackProps['justify']>, string> = {
  start: 'justify-start',
  between: 'justify-between',
  end: 'justify-end',
}

export function Stack({
  children,
  direccion = 'vertical',
  gap = 'sm',
  align,
  justify,
  wrap,
  as,
  className,
}: StackProps) {
  const Componente = (as ?? 'div') as ElementType
  const esLista = as === 'ul' || as === 'ol'

  return (
    <Componente
      data-testid="stack"
      className={cx(
        'flex min-w-0',
        direccion === 'vertical' ? 'flex-col' : 'flex-row',
        GAP[gap],
        align !== undefined && ALIGN[align],
        justify !== undefined && JUSTIFY[justify],
        wrap === true && 'flex-wrap',
        esLista && 'list-none p-0',
        className,
      )}
    >
      {children}
    </Componente>
  )
}
